import React, { useEffect, useMemo, useState } from 'react';
import { withSitecoreContext, RichText } from '@sitecore-jss/sitecore-jss-react';
import WebinarCard from '@rotaryintl/myrotary-webinar-card';
import Pagination from '@rotaryintl/harris-pagination';
import Link from '@rotaryintl/harris-link';
import { buildWebinarMetaLabel } from '../../utilities/format-webinar-date-helper-function';

const getPageFromUrl = () => {
  if (typeof window === 'undefined') return 1;
  const params = new URLSearchParams(window.location.search);
  const page = parseInt(params.get('page'), 10);
  return Number.isInteger(page) && page > 0 ? page : 1;
};

const WebinarsList = ({ sitecoreContext, fields }) => {
  const isExperienceEditor = sitecoreContext?.pageEditing === true;
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(getPageFromUrl());
  const [resultsCount, setResultsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const language = sitecoreContext?.language || 'en-US';
  const pageSize = useMemo(() => {
    const size = parseInt(fields?.PageSize?.value);
    return Number.isNaN(size) || size <= 0 ? 10 : size;
  }, [fields?.PageSize?.value]);

  const API_BASE_URL = fields?.ApiBaseUrl?.value || '/api/sitecore/WebinarSearch/GetWebinars';

  useEffect(() => {
    if (isExperienceEditor) return;
    const fetchWebinars = async () => {
      setLoading(true);

      try {
        const apiUrl = `${API_BASE_URL}?pageNumber=${pageNumber}&pageSize=${pageSize}&language=${language}`;
        const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
        'Accept-Language': 'en-US'},
      });
        if (!response.ok) {
          throw new Error('API request failed');
        }
        const data = await response.json();
        setItems(data?.Results || []);
        setResultsCount(data?.ResultsCount || 0);
      } catch (err) {
        console.error('Webinars API error', err);
        setItems([]);
        setResultsCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchWebinars();
  }, [pageNumber, pageSize, language, isExperienceEditor]);

  useEffect(() => {
    const handlePopState = () => {
      setPageNumber(getPageFromUrl());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handlePageChange = (value, maybePage) => {
    let nextPage = pageNumber;
    if (typeof value === 'number') nextPage = value;
    else if (value?.page) nextPage = value.page;
    else if (value?.selected) nextPage = value.selected;
    else if (typeof maybePage === 'number') nextPage = maybePage;
    if (!nextPage || nextPage === pageNumber) return;
    setPageNumber(nextPage);
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(nextPage));
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const style = document.createElement('style');
    style.innerHTML = `
    p{ margin-bottom : 0px; }
    .rwc-col-layout__title{ font-size: 38px;
          line-height: 45px;
          font-weight: 600; 
          margin-bottom : 0px !important; }
    @media (min-width: 768px) {
        .rwc-col-layout__title {
         font-size: 48px;
          line-height: 57px;
          font-weight: 600;}}
        @media (min-width: 992px) {
        .rwc-col-layout__title {
          font-size: 58px;
          line-height: 70px;
          font-weight: 600;}}
    .rwc-col-layout__col>:not([hidden])~:not([hidden]) {
      margin-top: calc(2rem*(1 - var(--tw-space-y-reverse))); }
    .rwc-col-layout { row-gap: 12px; }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const isNonEnglish = sitecoreContext?.language !== 'en-US';

  if (isExperienceEditor) {
    return (
      <div style={{ padding: 16, border: '1px dashed #ccc', background: '#fafafa' }}>
        <strong>Webinars List</strong>
        <p>Webinars load from API on the Page.</p>
      </div>
    );
  }

  return (
    <section style={{ minHeight: '500px' }}>
      {loading && <p>Loading webinars…</p>}
      {!loading && items.length > 0 && (
        <>
          <div>
            {items.map((item, index) => {
              const metaInfo = buildWebinarMetaLabel(item);
              const variant = metaInfo?.variant;
              const registerUrl = variant === 'upcoming' ? item.RegisterLink || undefined : undefined;
              const viewUrl = variant !== 'upcoming' ? item.ViewLink || undefined : undefined;
              const helperText = variant !== 'upcoming' && !item.ViewLink ? item.HelperText || undefined : undefined;
              return (
                <WebinarCard
                  key={index}
                  variant={metaInfo?.variant}
                  title={item.Title}
                  dateLabel={metaInfo?.variant === 'upcoming' ? metaInfo.meta : undefined}
                  onDemandMetaLabel={metaInfo?.variant === 'ondemand' ? metaInfo.meta : undefined}
                  bodyCopy={item.BodyCopy ? <div dangerouslySetInnerHTML={{ __html: item.BodyCopy }} /> : undefined}
                  primaryLanguage={item.PrimaryLanguage}
                  interpretedLanguages={item.InterpretedLanguages ? item.InterpretedLanguages.split(',').map((l) => l.trim()) : []}
                  registerLink={registerUrl}
                  viewLink={viewUrl}
                  showHelperText={helperText}
                />
              );
            })}
          </div>

          {resultsCount > pageSize && (
            <div>
              <Pagination
                currentPage={pageNumber}
                totalCount={resultsCount}
                pageSize={pageSize}
                siblingCount={1}
                lang={language}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
      {isNonEnglish && fields?.NoticeMessage && (
        <div style={{ display: 'flex', gap: '2px' }}>
          <RichText field={fields.NoticeMessage} />
          {fields?.NoticeLink?.value && <Link href={fields.NoticeLink.value}>{fields.NoticeLinkLabel.value}</Link>}
        </div>
      )}
    </section>
  );
};

export default withSitecoreContext()(WebinarsList); 
